import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import { Tag } from 'server/shared/models/tag';
import { DispoUser, User } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { ToastService } from '../../services/toast/toast.service';
import { SubscriptionService } from '../subscription.service';

@Component({
  selector: 'app-tag-select',
  templateUrl: './tag-select.component.html',
  styleUrls: ['./tag-select.component.scss'],
})
export class TagSelectComponent implements AfterViewInit, OnDestroy {
  @Input() selectedTags: Tag[] = [];

  allTags: Tag[] = [];
  loggedInUser?: User;
  customer?: Customer;

  searchTerm?: string;
  tagInputSubscription?: Subscription;
  private destroyed$ = new Subject<void>();
  filteredTags: Tag[] = [];
  newTagCreationString = 'createNewTag';

  private userTracker?: Tracker.Computation;

  @ViewChild('tagInput', { static: false }) tagInput?: NgControl;
  @ViewChild('autoTags', { static: false }) autoTags?: MatAutocomplete;

  @Output() tagAdded = new EventEmitter<string>();
  @Output() tagRemoved = new EventEmitter<string>();
  @Output() addNewlyCreatedTag = new EventEmitter<Tag>();
  @Output() tagCreated = new EventEmitter<string>();

  constructor(
    private toastService: ToastService,
    private ngZone: NgZone,
    private subscriptionService: SubscriptionService,
    private customerService: CustomerService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer = customer;
        this.userTracker = Tracker.autorun(() => {
          this.loggedInUser = Users.findOne({
            _id: Meteor.userId(),
            role: RoleEnum.DISPO,
          }) as DispoUser;
          if (this.customer?.tags) {
            this.allTags = this.customer.tags;
          }
          this.filterTags();
        });
      });
  }

  ngAfterViewInit(): void {
    this.filterTags();
    this.tagInputSubscription = this.tagInput?.valueChanges?.subscribe(
      (value) => {
        if (value === this.newTagCreationString) {
          return;
        }
        if (typeof value == 'string') {
          this.searchTerm = value;
        } else {
          delete this.searchTerm;
        }
        this.filterTags();
      },
    );
  }

  ngOnDestroy() {
    if (this.tagInputSubscription) {
      this.tagInputSubscription.unsubscribe();
    }
    if (this.userTracker) {
      this.userTracker.stop();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private filterTags() {
    let filteredTags = this.allTags;
    filteredTags.filter(
      (tag) => !this.selectedTags?.map((t) => t.id).includes(tag.id),
    );
    if (this.searchTerm) {
      filteredTags = filteredTags?.filter((tag) =>
        tag.displayName.toLowerCase().includes(this.searchTerm!.toLowerCase()),
      );
    }
    filteredTags = filteredTags?.sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );

    this.ngZone.run(() => {
      this.filteredTags = filteredTags;
    });
  }

  createNewTag() {
    const newTag = {
      displayName: this.searchTerm || '',
      id: randomId(),
    };
    this.resetAutoComplete();
    this.addNewlyCreatedTag.emit(newTag);

    if (!this.customer) return;
    Meteor.call(
      'createNewTag',
      this.customer._id,
      newTag,
      (error: Meteor.Error) => {
        if (error) {
          if (error.error === 'tagAlreadyExists') {
            this.toastService.add({
              type: 'ERROR',
              message: 'tagAlreadyExists',
            });
          } else {
            this.toastService.add({
              type: 'ERROR',
              message: 'couldntCreateTag',
            });
          }
        } else {
          setTimeout(() => {
            this.tagCreated.emit(newTag.id);
          });
        }
      },
    );
  }

  resetAutoComplete() {
    setTimeout(() => {
      this.searchTerm = '';
      // @ts-ignore
      this.tagInput.control.nativeElement.blur();
      // @ts-ignore
      this.tagInput.control.nativeElement.value = '';
      this.tagInput?.reset('');
      this.tagInput?.control?.markAsDirty();
      this.autoTags?.options.forEach((item) => {
        item.deselect();
      });
    });
  }

  addTag(matSelectedEvent: MatAutocompleteSelectedEvent) {
    this.resetAutoComplete();
    const value = matSelectedEvent.option.value;
    if (value === this.newTagCreationString) {
      this.createNewTag();
      return;
    }
    this.tagAdded.emit(matSelectedEvent.option.value);
  }
  removeTag(tagId: string) {
    this.tagRemoved.emit(tagId);
  }
  destroyTag(tagId: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.customer) return;
    Meteor.call(
      'deleteTag',
      this.customer._id,
      tagId,
      (error: Meteor.Error) => {
        if (error) {
          console.log(error);
          if (error.error === 'tagStillInUse') {
            this.toastService.add({
              type: 'ERROR',
              message: 'tagStillInUse',
            });
          } else {
            this.toastService.add({
              type: 'ERROR',
              message: 'couldntDeleteTag',
            });
          }
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: 'successfullyDeletedTag',
          });
        }
      },
    );
  }

  trackByTagId(_index: number, tag: Tag): string {
    return tag.id;
  }
}
